import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {
  applyPendingProductPriceFromSession,
  clareProductToProduct,
  InventoryVariation,
  InventoryVariationPatch,
  mergeVariationsIfMissing,
  PagedApiResponse,
  Product,
  ProductSpec,
} from '../../models/clare';
import {environment} from '../../../environments/environment';
import {ProductQueryParams, productQueryParamsToItemsQueryParams} from '../../models';
import {objectToHttpParams, pluckResult, ProductManagementApiService} from '../product-management-api.service';
import {ClareApiResponse, PagedClareApiResponse, Variation} from '@sportngin/se-commerce-sdk/models/clare';

export interface ProductApi {
  get(itemId: string, organizationId?: string): Observable<Product>;

  /**
   * GET /finance/products/:id (or /items/:id); if the body has `variations: []` but `default_variation_id`
   * is set, merges GET …/:id/variations for price resolution. Remove when GET …/:id includes variation
   * rows (or embedded default) with price.
   */
  getWithMergedVariations(itemId: string, organizationId?: string): Observable<Product>;

  getProducts(
    catalogId: number | undefined,
    queryParams: ProductQueryParams,
    organizationId?: string
  ): Observable<PagedApiResponse<Product>>;

  getProductsByOrganization(
    organizationId: string,
    queryParams: ProductQueryParams
  ): Observable<PagedApiResponse<Product>>;

  getProductsByCatalog(catalogId: number, queryParams: ProductQueryParams): Observable<PagedApiResponse<Product>>;

  update(product: Partial<Product>, organizationId?: string): Observable<Product>;

  createProduct(product: Product, organizationId?: string): Observable<Product>;

  generateProductVariants(productId: string | number, overwriteExistingVariants?: boolean): Observable<Variation[]>;

  getProductVariants(productId: string): Observable<Variation[]>;

  /**
   * GET /finance/product_variations/:id (or /finance/item_variations/:id) — single variation.
   */
  getProductVariant(variationId: string): Observable<Variation>;

  updateVariantStatus(args: {id: string; active: boolean; productId?: string}): Observable<Variation>;

  getProductSpecs(productId: string): Observable<ProductSpec[]>;

  updateProductVariant(
    variant: Partial<Variation> | InventoryVariationPatch,
    productId?: string
  ): Observable<InventoryVariation>;

  updateGenerateVariants(itemId: number): Observable<Variation[]>;
}

@Injectable({
  providedIn: 'root',
})
export class ProductApiService extends ProductManagementApiService implements ProductApi {
  readonly controllerPath = '';

  constructor(protected override http: HttpClient) {
    super(http);
  }

  private get itemControllerBasePath(): string {
    return '/finance/products';
  }

  private get variationControllerBasePath(): string {
    return '/finance/product_variations';
  }

  get(productId: string, organizationId?: string): Observable<Product> {
    const url = this.getUrl(this.productPath(productId, organizationId));
    return this.GET<ClareApiResponse<Product>>(url, this.requestOptions()).pipe(
      pluckResult(),
      map(applyPendingProductPriceFromSession),
      map(clareProductToProduct)
    );
  }

  /**
   * GET /finance/products/:id (or /items/:id); if the body has `variations: []` but `default_variation_id`
   * is set, merges GET …/:id/variations for price resolution. Remove when GET …/:id includes variation
   * rows (or embedded default) with price.
   */
  getWithMergedVariations(productId: string, organizationId?: string): Observable<Product> {
    return this.GET<ClareApiResponse<Product>>(
      this.getUrl(this.productPath(productId, organizationId)),
      this.requestOptions()
    ).pipe(
      pluckResult(),
      switchMap(item =>
        mergeVariationsIfMissing(
          item,
          id => this.getProductVariants(id),
          id => this.getProductVariant(id)
        )
      ),
      map(applyPendingProductPriceFromSession),
      map(clareProductToProduct)
    );
  }

  getProducts(
    catalogId: number | undefined,
    queryParams: ProductQueryParams,
    organizationId?: string
  ): Observable<PagedApiResponse<Product>> {
    return this.loadProducts(queryParams, {catalog_id: catalogId, organization_id: organizationId});
  }

  getProductsByOrganization(
    organizationId: string,
    queryParams: ProductQueryParams
  ): Observable<PagedApiResponse<Product>> {
    return this.loadProducts(queryParams, {organization_id: organizationId});
  }

  getProductsByCatalog(catalogId: number, queryParams: ProductQueryParams): Observable<PagedApiResponse<Product>> {
    return this.loadProducts(queryParams, {catalog_id: catalogId});
  }

  private loadProducts(
    queryParams: ProductQueryParams,
    scope: {catalog_id?: number; organization_id?: string}
  ): Observable<PagedApiResponse<Product>> {
    const params = productQueryParamsToItemsQueryParams(queryParams);

    const url = this.getUrl(this.itemControllerBasePath);

    return this.GET<PagedClareApiResponse<Product>>(url, {
      ...this.requestOptions(),
      params: objectToHttpParams({...params, ...scope}),
    }).pipe(
      map((response: PagedClareApiResponse<Product>) => ({
        ...response,
        result: response.result.map(applyPendingProductPriceFromSession).map(clareProductToProduct),
      }))
    );
  }

  update(product: Partial<Product>, organizationId?: string): Observable<Product> {
    const path = this.productPath(product.id, organizationId);
    const url = this.getUrl(path);
    return this.PATCH<ClareApiResponse<Product>>(url, product, this.requestOptions()).pipe(
      pluckResult(),
      map(clareProductToProduct)
    );
  }

  createProduct(product: Product, organizationId: string): Observable<Product> {
    const url = this.getUrl(this.itemControllerBasePath);

    return this.POST<ClareApiResponse<Product>>(url, product, {
      ...this.requestOptions(),
      params: {organization_id: organizationId},
    }).pipe(pluckResult(), map(clareProductToProduct));
  }

  generateProductVariants(productId: number, __overwriteExistingVariants = false): Observable<Variation[]> {
    const url = this.getUrl(`${this.itemControllerBasePath}/${productId}/variation_generation`);

    return this.POST<ClareApiResponse<Variation[]>>(url, null, {...this.requestOptions()}).pipe(pluckResult());
  }

  updateGenerateVariants(productId: number): Observable<Variation[]> {
    const url = this.getUrl(`${this.itemControllerBasePath}/${productId}/variation_generation`);

    return this.PUT<ClareApiResponse<Variation[]>>(url, null, {...this.requestOptions()}).pipe(pluckResult());
  }

  getProductVariant(variationId: string): Observable<Variation> {
    const url = this.getUrl(`${this.variationControllerBasePath}/${variationId}`);
    return this.GET<ClareApiResponse<Variation>>(url, this.requestOptions()).pipe(pluckResult());
  }

  getProductVariants(productId: string): Observable<Variation[]> {
    const url = this.getUrl(`${this.itemControllerBasePath}/${productId}/variations`);
    return this.GET<PagedClareApiResponse<Variation>>(url, this.requestOptions()).pipe(pluckResult());
  }

  updateVariantStatus(args: {id: string; active: boolean}): Observable<Variation> {
    const url = this.getUrl(`${this.variationControllerBasePath}/${args.id}`);
    return this.PATCH<ClareApiResponse<Variation>>(
      url,
      {active: args.active, visible: args.active},
      this.requestOptions()
    ).pipe(pluckResult());
  }

  getProductSpecs(productId: string): Observable<ProductSpec[]> {
    const url = this.getUrl(`${this.itemControllerBasePath}/${productId}/product_specs`);
    return this.GET<ClareApiResponse<ProductSpec[]>>(url, this.requestOptions()).pipe(pluckResult());
  }

  updateProductVariant(variant: Partial<Variation> | InventoryVariationPatch): Observable<InventoryVariation> {
    const variantId = variant.id;
    if (variantId === undefined || variantId === null) {
      throw new Error('updateProductVariant requires variant.id');
    }
    const url = this.getUrl(`${this.variationControllerBasePath}/${variantId}`);
    return this.PATCH<ClareApiResponse<InventoryVariation>>(url, variant, this.requestOptions()).pipe(pluckResult());
  }

  private productPath(productId: string | number | undefined | null, organizationId?: string): string {
    return organizationId
      ? `/finance/organizations/${organizationId}/products/${productId}`
      : `${this.itemControllerBasePath}/${productId}`;
  }

  protected override getUrl(path = ''): string {
    return `${environment.apiBaseUrl}${path}`;
  }
}
